import { Temporal } from '@js-temporal/polyfill';

import type { Shift } from './api/crew.ts';
import { answerOf, type Offer } from './api/offers.ts';
import type { RosterPage } from './api/roster.ts';
import type { Task } from './api/tasks.ts';

// The roster's rows, kept apart from the screen so they can be tested (CB-REQ OPS-03 at v1.998,
// CB-CONV §12 crew part 4B). Tasks above, a band per job and a "No job" band last; then people, a
// row each, those busy in the window first, then everyone else, each part by name; then the
// shifts nobody holds, by role.

export interface TaskBand {
  readonly key: string;
  readonly job: Task['job'];
  readonly tasks: Task[];
}

export interface PersonRow {
  readonly key: string;
  readonly label: string;
  /** "Unfilled" for the shifts nobody holds. */
  readonly sub?: string;
  readonly shifts: Shift[];
  /** Offers of places this person has yet to answer, while the offer is open (CRW-04). */
  readonly offers: Offer[];
}

const startOf = (t: { window: { startsAt: string } }) => Temporal.Instant.from(t.window.startsAt);

/** A band per job, the soonest first; tasks on no job last. */
export function taskBands(tasks: readonly Task[]): TaskBand[] {
  const bands = new Map<string, TaskBand>();
  for (const t of tasks) {
    const key = t.job?.id ?? 'no-job';
    const band = bands.get(key) ?? { key, job: t.job ?? null, tasks: [] };
    band.tasks.push(t);
    bands.set(key, band);
  }
  const soonest = (b: TaskBand) =>
    b.tasks.reduce(
      (min, t) => (Temporal.Instant.compare(startOf(t), min) < 0 ? startOf(t) : min),
      startOf(b.tasks[0]!),
    );
  return [...bands.values()].sort((a, b) => {
    if (!a.job !== !b.job) return a.job ? -1 : 1;
    return (
      Temporal.Instant.compare(soonest(a), soonest(b)) ||
      (a.job?.number ?? 0) - (b.job?.number ?? 0)
    );
  });
}

/**
 * A task bar as a progress bar: filled by the people on it, at most full — over-filled is just
 * full. Covered is full whoever is on it; done is drawn in the done colour, struck through.
 */
export function taskProgress(t: Pick<Task, 'filled' | 'crewNeeded' | 'status'>): {
  fill: number;
  covered: boolean;
  done: boolean;
} {
  const covered = t.status === 'covered';
  return {
    fill: covered ? 1 : Math.min(t.filled / t.crewNeeded, 1),
    covered,
    done: t.status === 'done',
  };
}

/** Everyone on the crew list, busy first; then the shifts nobody holds, by role. */
export function peopleRows(page: Pick<RosterPage, 'people' | 'shifts' | 'offers'>): PersonRow[] {
  const people = new Map<string, PersonRow>();
  const person = (id: string, name: string) => {
    const row = people.get(id) ?? { key: id, label: name, shifts: [], offers: [] };
    people.set(id, row);
    return row;
  };
  for (const p of page.people) person(p.id, p.name);
  const open = new Map<string, PersonRow>();
  for (const s of page.shifts) {
    // A declined shift is a gap to fill again: it sits with the unfilled ones.
    if (s.crewMember && s.state !== 'declined') {
      person(s.crewMember.id, s.crewMember.name).shifts.push(s);
    } else {
      const roleKey = s.role?.id ?? 'held';
      const row = open.get(roleKey) ?? {
        key: `open-${roleKey}`,
        label: s.role?.name ?? 'Held day',
        sub: 'Unfilled',
        shifts: [],
        offers: [],
      };
      row.shifts.push(s);
      open.set(roleKey, row);
    }
  }
  for (const o of page.offers) {
    if (!o.open) continue;
    for (const r of o.recipients) {
      if (answerOf(r) === 'Waiting') person(r.crewMember.id, r.crewMember.name).offers.push(o);
    }
  }
  const busy = (r: PersonRow) => r.shifts.length + r.offers.length > 0;
  const byLabel = (a: PersonRow, b: PersonRow) => a.label.localeCompare(b.label);
  const everyone = [...people.values()];
  return [
    ...everyone.filter(busy).sort(byLabel),
    ...everyone.filter((r) => !busy(r)).sort(byLabel),
    ...[...open.values()].sort(byLabel),
  ];
}

const GRACE_MINUTES = 15;

/** "2h 30m", "45m", "1h". */
function lasting(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return [h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ') || '0m';
}

/**
 * Why a task doesn't fit a shift, as the design's confirm words it (OPS-10). The database judges the
 * fit, with 15 minutes' grace either end; this only says how, in the office's words.
 */
export function misfitReason(
  task: { startsAt: string; endsAt: string },
  shift: { startsAt: string; endsAt: string },
  venueDiffers: boolean,
): string {
  const at = (s: string) => Temporal.Instant.from(s).epochMilliseconds / 60_000;
  const [ts, te, ss, se] = [
    at(task.startsAt),
    at(task.endsAt),
    at(shift.startsAt),
    at(shift.endsAt),
  ];
  const why: string[] = [];
  if (te <= ss) why.push('Ends before the shift starts');
  else if (ts >= se) why.push('Starts after the shift ends');
  else {
    if (ss - ts > GRACE_MINUTES) why.push(`Starts ${lasting(ss - ts)} before the shift`);
    if (te - se > GRACE_MINUTES) why.push(`Runs ${lasting(te - se)} past the shift`);
  }
  if (venueDiffers) why.push('At another venue');
  return why.join(' · ') || 'Fits';
}

/** Hours a person's live shifts take up inside the window: "12.00h". */
export function hoursInView(
  shifts: readonly Shift[],
  span: { from: Temporal.Instant; to: Temporal.Instant },
): number {
  let minutes = 0;
  for (const s of shifts) {
    if (s.state === 'declined' || s.state === 'cancelled') continue;
    const from = Math.max(
      Temporal.Instant.from(s.window.startsAt).epochMilliseconds,
      span.from.epochMilliseconds,
    );
    const to = Math.min(
      Temporal.Instant.from(s.window.endsAt).epochMilliseconds,
      span.to.epochMilliseconds,
    );
    if (to > from) minutes += (to - from) / 60_000;
  }
  return minutes / 60;
}
